import { AllIntegrations } from '@/hooks/useIntegration';
import { RiskWithVulnerability } from '@/hooks/useRisks';
import type { DropdownOption } from '@/types/dropdown.types';

export type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

/**
 * Transforms raw vulnerability source data into user-friendly display text.
 *
 * This function handles complex logic for displaying vulnerability sources based on:
 * - User type (Praetorian internal vs. customer)
 * - Organization type (demo vs. production)
 * - Source type (integration, manual, internal tool)
 * - Source grouping (Chariot-grouped sources)
 *
 * @param risk - The vulnerability risk object containing source information
 * @param isPraetorianUser - Whether the current user is a Praetorian employee
 * @param isDemoOrg - Whether the current organization is a demo account
 * @param allIntegrationsObj - Object containing all available integration definitions
 * @param sourceFilterOptions - Array of source filter options including grouped sources
 * @returns User-friendly display text for the vulnerability source
 *
 * @example
 * // Integration source
 * getDisplaySourceText(
 *   { source: 'nuclei', ... },
 *   false,
 *   false,
 *   AllIntegrations,
 *   sourceFilterOptions
 * )
 * // Returns: "Nuclei"
 *
 * @example
 * // Manual entry
 * getDisplaySourceText(
 *   { source: 'user@praetorian.com', ... },
 *   true,
 *   false,
 *   AllIntegrations,
 *   sourceFilterOptions
 * )
 * // Returns: "Manual" (for Praetorian users) or "Praetorian" (for customers/demo orgs)
 *
 * @example
 * // Internal tool (customer view)
 * getDisplaySourceText(
 *   { source: 'internal-scanner', ... },
 *   false,
 *   false,
 *   AllIntegrations,
 *   sourceFilterOptions
 * )
 * // Returns: "Chariot"
 */
export function getDisplaySourceText(
  risk: RiskWithVulnerability,
  isPraetorianUser: boolean,
  isDemoOrg: boolean,
  allIntegrationsObj: typeof AllIntegrations,
  sourceFilterOptions: GroupOption[]
): string {
  const rawSource = risk.source;
  if (!rawSource) {
    return '-';
  }

  const lowerSource = rawSource.toLowerCase();
  const isIntegration = Object.keys(allIntegrationsObj).includes(lowerSource);
  const isManual = rawSource.endsWith('@praetorian.com');

  const getFormattedLabel = (sourceValue: string): string => {
    let foundLabel: string | undefined;

    const chariotGroupFromFilters = sourceFilterOptions.find(
      sf => sf.isGroup && sf.label === 'Praetorian'
    );
    if (chariotGroupFromFilters?.items) {
      foundLabel = chariotGroupFromFilters.items.find(
        item => item.value === sourceValue
      )?.label;
    }

    if (!foundLabel) {
      const integrationOption = sourceFilterOptions.find(
        option => !option.isGroup && option.value === sourceValue
      );
      foundLabel = integrationOption?.label;
    }

    return (
      foundLabel ||
      sourceValue.charAt(0).toUpperCase() + sourceValue.slice(1).toLowerCase()
    );
  };

  const chariotGroup = sourceFilterOptions.find(
    sf => sf.isGroup && sf.label === 'Praetorian'
  );
  const chariotSourceValues =
    chariotGroup?.items?.map(item => item.value) || [];
  const isChariotGroupedSource = chariotSourceValues.includes(rawSource);

  if (isManual) {
    return isPraetorianUser && !isDemoOrg ? 'Manual' : 'Praetorian';
  }

  if (isIntegration) {
    return getFormattedLabel(rawSource);
  }

  if (isDemoOrg) {
    return 'Praetorian';
  }

  // Check if source is in Praetorian group BEFORE checking Praetorian user
  // This ensures Praetorian-grouped sources always display as "Praetorian"
  if (isChariotGroupedSource) {
    return 'Praetorian';
  }

  if (isPraetorianUser) {
    return getFormattedLabel(rawSource);
  }

  return 'Praetorian';
}
